import { useEffect, useState, useCallback, useMemo } from "react";
import StaffShell from "../components/StaffShell";
import { T, useI18n } from "../lib/i18n";
import api, { formatErr, fileUrl } from "../lib/api";
import { useEventSocket } from "../lib/ws";
import { useMotif } from "../contexts/MotifContext";
import { toast } from "sonner";
import { HelpCircle, Megaphone, Check, EyeOff, Eye, Image as ImageIcon, MapPin, Utensils } from "lucide-react";
import Lightbox from "../components/Lightbox";
import PhotoQueuePanel from "../components/PhotoQueuePanel";
import EmbroideryPanel from "../components/EmbroideryPanel";
import MomentsComposer from "../components/MomentsComposer";
import AddGuestButton from "../components/AddGuestButton";
import KitchenQueue from "../components/KitchenQueue";
import VenueMapModal from "../components/VenueMapModal";
import { VipBadge } from "../components/VipBadge";

export default function CoordinatorDash() {
  useI18n();
  const { eventId } = useMotif();
  const [helps, setHelps] = useState([]);
  const [others, setOthers] = useState([]);
  const [feed, setFeed] = useState([]);
  const [guestIndex, setGuestIndex] = useState({});
  const [mapTarget, setMapTarget] = useState(null);

  // Phase 5E: pin a ticket's guest table on the currently active map
  const viewOnMap = (ticket) => {
    const g = guestIndex[ticket.guest_id] || {};
    const tableName = g.table_name || ticket.table_name || "";
    const digits = String(tableName).replace(/\D/g, "");
    const name = g.full_name || ticket.guest_name || "Guest";
    if (!digits) {
      toast(T("Table location unavailable for this guest."));
      return;
    }
    const seat = g.seat ? `, Seat ${g.seat}` : "";
    setMapTarget({
      type: "MY_TABLE",
      tableNumber: Number(digits),
      pinLabel: `${name} — Table ${digits}${seat}`,
    });
  };

  const refresh = useCallback(async () => {
    const [h, o, f, g] = await Promise.allSettled([
      api.get("/requests/help"),
      api.get("/requests/food-drink", { params: { category: "other" } }),
      api.get("/feed", { params: { approved_only: false } }),
      api.get("/guests"),
    ]);
    if (h.status === "fulfilled") setHelps(h.value.data);
    if (o.status === "fulfilled") setOthers(o.value.data);
    if (f.status === "fulfilled") setFeed(f.value.data);
    if (g.status === "fulfilled") {
      const idx = {};
      g.value.data.forEach((gu) => { idx[gu.id] = gu; });
      setGuestIndex(idx);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);
  useEventSocket(eventId, (msg) => {
    if (msg.type === "help_request_new") {
      setHelps((h) => [msg.payload, ...h]);
      toast(`Help: ${msg.payload.kind} · Table ${msg.payload.table_name}`, { icon: "🛟" });
    }
    if (msg.type === "help_request_update") setHelps((h) => h.map((x) => (x.id === msg.payload.id ? msg.payload : x)));
    if (msg.type === "fd_request_new" && msg.payload?.category === "other") {
      setOthers((o) => [msg.payload, ...o]);
      toast(`Request: ${msg.payload.item} · Table ${msg.payload.table_name}`, { icon: "🔔" });
    }
    if (msg.type === "fd_request_update" && msg.payload?.category === "other") {
      setOthers((o) => o.map((x) => (x.id === msg.payload.id ? msg.payload : x)));
    }
    if (msg.type === "feed_new" || msg.type === "feed_update" || msg.type === "ws_reconnected") refresh();
  });

  const updateHelp = async (id, status) => {
    try { await api.put(`/requests/help/${id}/status`, { status }); refresh(); }
    catch (e) { toast.error(formatErr(e)); }
  };
  const updateOther = async (id, status) => {
    try { await api.put(`/requests/food-drink/${id}/status`, { status }); refresh(); }
    catch (e) { toast.error(formatErr(e)); }
  };
  const moderate = async (id, approved) => {
    try { await api.put(`/feed/${id}/approve`, { approved }); refresh(); }
    catch (e) { toast.error(formatErr(e)); }
  };

  const [lbIndex, setLbIndex] = useState(-1);
  const photoPosts = useMemo(() => feed.filter((p) => p.image_url), [feed]);
  const lbItems = useMemo(
    () => photoPosts.map((p) => ({ src: fileUrl(p.image_url), caption: p.caption, author: p.author_name })),
    [photoPosts]
  );
  const openLb = (postId) => {
    const i = photoPosts.findIndex((p) => p.id === postId);
    if (i >= 0) setLbIndex(i);
  };

  const [helpLbIndex, setHelpLbIndex] = useState(-1);
  const helpsWithPhotos = useMemo(() => helps.filter((h) => h.image_url), [helps]);
  const helpLbItems = useMemo(
    () => helpsWithPhotos.map((h) => ({ src: fileUrl(h.image_url), caption: `${h.kind}: ${h.notes || ""}`, author: h.guest_name })),
    [helpsWithPhotos]
  );
  const openHelpLb = (hid) => {
    const i = helpsWithPhotos.findIndex((h) => h.id === hid);
    if (i >= 0) setHelpLbIndex(i);
  };

  // Critical severity outranks VIP; then VIP > non-VIP; then oldest first
  const openHelps = useMemo(() => {
    const sev = (r) => (r.severity === "critical" ? 0 : 1);
    return helps
      .filter((h) => h.status !== "resolved")
      .sort((a, b) => {
        if (sev(a) !== sev(b)) return sev(a) - sev(b);
        const av = a.vip ? 0 : 1, bv = b.vip ? 0 : 1;
        if (av !== bv) return av - bv;
        return (a.created_at || "").localeCompare(b.created_at || "");
      });
  }, [helps]);

  const openOthers = useMemo(() => others.filter((o) => o.status !== "delivered"), [others]);

  return (
    <StaffShell title={T("Coordinator")} subtitle={T("Floor in your hand")}>
      <div className="mb-4 flex justify-end">
        <AddGuestButton onAdded={refresh} />
      </div>
      {mapTarget && (
        <VenueMapModal behavior={mapTarget} itemName={mapTarget.pinLabel} onClose={() => setMapTarget(null)} />
      )}
      <div className="mb-5">
        {/* Help queue */}
        <div className="card-elev p-5">
          <div className="flex items-center gap-2 mb-3"><HelpCircle className="w-4 h-4 text-primary-motif" /><div className="font-display text-xl">{T("Help queue")}</div></div>
          <div className="space-y-2">
            {openHelps.map((h) => (
              <div key={h.id} className="p-3 rounded-xl bg-accent-motif" data-testid={`help-row-${h.id}`}>
                <div className="font-display text-base text-motif flex items-center gap-2">
                  <VipBadge show={!!h.vip} />
                  {h.severity === "critical" && (
                    <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full" style={{ background: "#ef444420", color: "#ef4444", border: "1px solid #ef4444" }} data-testid={`help-critical-${h.id}`}>{T("CRITICAL")}</span>
                  )}
                  {h.kind}
                </div>
                <div className="text-[10px] text-motif-muted">{h.guest_name} · Table {h.table_name}</div>
                {h.notes && <div className="text-xs italic mt-1">{h.notes}</div>}
                {h.image_url && (
                  <button
                    type="button"
                    onClick={() => openHelpLb(h.id)}
                    className="block mt-2"
                    data-testid={`help-img-${h.id}`}
                  >
                    <img src={fileUrl(h.image_url)} alt="" className="w-full h-24 object-cover rounded-lg" loading="lazy" />
                  </button>
                )}
                <div className="flex gap-2 mt-2">
                  <button onClick={() => updateHelp(h.id, "in_progress")} className="btn-ghost text-xs">{T("Take")}</button>
                  <button onClick={() => updateHelp(h.id, "resolved")} className="btn-primary text-xs px-3 py-1.5" data-testid={`help-resolve-${h.id}`}>
                    <Check className="w-3 h-3 inline mr-1" />Resolved
                  </button>
                  <button onClick={() => viewOnMap(h)} className="btn-ghost text-xs flex items-center gap-1" data-testid={`help-map-${h.id}`}>
                    <MapPin className="w-3 h-3" /> {T("View on Map")}
                  </button>
                </div>
              </div>
            ))}
            {openHelps.length === 0 && <div className="text-motif-muted text-sm">{T("All clear.")}</div>}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-5">
        <MomentsComposer />
        <PhotoQueuePanel />
      </div>

      <div className="mb-5">
        <EmbroideryPanel onViewMap={viewOnMap} />
      </div>

      {/* Phase 5E: Kitchen assist — same live queue and controls as the Kitchen dash */}
      <div className="card-elev p-5 mb-5">
        <div className="flex items-center gap-2 mb-3">
          <Utensils className="w-4 h-4 text-primary-motif" />
          <div className="font-display text-xl">{T("Kitchen line")}</div>
          <span className="text-[10px] tracking-luxe text-motif-muted ml-2">{T("assist the kitchen — live tickets")}</span>
        </div>
        <KitchenQueue embedded />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Other requests (announcements merged into the Announce · Moments card above) */}
        <div className="card-elev p-5">
          <div className="flex items-center gap-2 mb-3"><Megaphone className="w-4 h-4 text-primary-motif" /><div className="font-display text-xl">{T("Other Requests")}</div></div>
          <div>
            {openOthers.map((o) => (
              <div key={o.id} className="p-2 rounded-xl bg-accent-motif mb-2" data-testid={`other-row-${o.id}`}>
                <div className="text-sm font-medium">{o.item}</div>
                <div className="text-[10px] text-motif-muted">{o.guest_name} · Table {o.table_name}</div>
                <div className="flex gap-2 mt-1">
                  <button onClick={() => updateOther(o.id, "delivered")} className="btn-ghost text-xs">{T("Mark done")}</button>
                  <button onClick={() => viewOnMap(o)} className="btn-ghost text-xs flex items-center gap-1" data-testid={`other-map-${o.id}`}>
                    <MapPin className="w-3 h-3" /> {T("View on Map")}
                  </button>
                </div>
              </div>
            ))}
            {openOthers.length === 0 && <div className="text-motif-muted text-xs">{T("No other requests.")}</div>}
          </div>
        </div>

        {/* Feed moderation */}
        <div className="card-elev p-5">
          <div className="flex items-center gap-2 mb-3"><Eye className="w-4 h-4 text-primary-motif" /><div className="font-display text-xl">{T("Feed moderation")}</div></div>
          <div className="space-y-2 max-h-[60vh] overflow-y-auto">
            {feed.map((p) => (
              <div key={p.id} className="p-2 rounded-xl border border-motif" data-testid={`mod-row-${p.id}`}>
                {p.image_url && (
                  <button
                    type="button"
                    onClick={() => openLb(p.id)}
                    className="block w-full"
                    data-testid={`mod-img-btn-${p.id}`}
                  >
                    <img src={fileUrl(p.image_url)} alt="" className="w-full h-32 object-cover rounded-lg mb-2" loading="lazy" />
                  </button>
                )}
                <div className="text-xs">{p.author_name}: {p.caption}</div>
                <button
                  onClick={() => moderate(p.id, !p.approved)}
                  className="btn-ghost text-xs mt-2 flex items-center gap-1"
                  data-testid={`mod-toggle-${p.id}`}
                >
                  {p.approved ? <><EyeOff className="w-3 h-3" />{T("Hide")}</> : <><Eye className="w-3 h-3" />{T("Show")}</>}
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
      <Lightbox
        open={lbIndex >= 0}
        items={lbItems}
        index={lbIndex < 0 ? 0 : lbIndex}
        onClose={() => setLbIndex(-1)}
        onIndex={setLbIndex}
      />
      <Lightbox
        open={helpLbIndex >= 0}
        items={helpLbItems}
        index={helpLbIndex < 0 ? 0 : helpLbIndex}
        onClose={() => setHelpLbIndex(-1)}
        onIndex={setHelpLbIndex}
      />
    </StaffShell>
  );
}
